import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, 
  User, 
  Phone, 
  Mail,
  Calendar,
  Edit,
  Trash2,
  Clock,
  FileText,
  DollarSign,
  ShoppingCart,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const referralLabels = {
  recomendacion: 'Recomendación de otro paciente',
  redes_sociales: 'Redes Sociales',
  google: 'Google / Búsqueda',
  publicidad: 'Publicidad',
  clinica_cercana: 'Clínica cercana',
  seguro_medico: 'Seguro Médico',
  otro: 'Otro'
};

const PatientDetailPage = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [sales, setSales] = useState([]);
  const [totalSpent, setTotalSpent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [patientRes, appointmentsRes, salesRes] = await Promise.all([
        axios.get(`${API}/patients/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/appointments`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/sales/?paciente_id=${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      
      setPatient(patientRes.data);
      setEditData(patientRes.data);
      
      // Filter appointments for this patient
      const patientAppointments = appointmentsRes.data.filter(
        apt => apt.patient_id === id || apt.patient_phone === patientRes.data.phone
      );
      setAppointments(patientAppointments);
      
      // Set sales (already filtered by backend)
      setSales(salesRes.data || []);
      
      // Calculate total spent
      const total = (salesRes.data || [])
        .filter(s => s.estado_pago !== 'ANULADA')
        .reduce((sum, s) => sum + s.total, 0);
      setTotalSpent(total);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/patients/${id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEdit(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar paciente');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/patients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/admin/patients');
    } catch (error) {
      console.error('Delete error:', error);
      alert(error.response?.data?.detail || 'Error al eliminar paciente');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getStatusBadge = (status) => {    const styles = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      attended: 'status-attended',
      cancelled: 'status-cancelled',
      no_show: 'status-no_show'
    };
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      attended: 'Atendida',
      cancelled: 'Cancelada',
      no_show: 'No asistió'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Paciente no encontrado</p>
        <Button variant="link" onClick={() => navigate('/admin/patients')}>
          Volver a pacientes
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patient-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/patients')}
          className="p-2"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]">{patient.full_name}</h1>
          <p className="text-slate-500 mt-1">Perfil del paciente</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="edit-patient-button">
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-patient-button">
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Info */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Información</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {patient.codigo_paciente && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <FileText className="h-5 w-5 text-[#0891B2]" />
                  <div>
                    <p className="text-sm text-slate-500">Código de paciente</p>
                    <p className="font-medium font-mono">{patient.codigo_paciente}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <User className="h-5 w-5 text-[#0891B2]" />
                <div>
                  <p className="text-sm text-slate-500">Nombre</p>
                  <p className="font-medium">{patient.full_name}</p>
                </div>
              </div>

              {patient.birth_date && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Calendar className="h-5 w-5 text-[#0891B2]" />
                  <div>
                    <p className="text-sm text-slate-500">Fecha de nacimiento</p>
                    <p className="font-medium">
                      {format(new Date(patient.birth_date + 'T12:00:00'), "dd/MM/yyyy")}
                      {(() => {
                        const age = calculateAge(patient.birth_date);
                        return age != null ? ` · ${age} años` : '';
                      })()}
                    </p>
                  </div>
                </div>
              )}

              {patient.sex && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <User className="h-5 w-5 text-[#0891B2]" />
                  <div>
                    <p className="text-sm text-slate-500">Sexo</p>
                    <p className="font-medium capitalize">{patient.sex}</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Phone className="h-5 w-5 text-[#0891B2]" />
                <div>
                  <p className="text-sm text-slate-500">Teléfono</p>
                  <p className="font-medium">{patient.phone}</p>
                </div>
              </div>

              {patient.alternate_phone && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="h-5 w-5 text-[#0891B2]" />
                  <div>
                    <p className="text-sm text-slate-500">Teléfono alterno</p>
                    <p className="font-medium">{patient.alternate_phone}</p>
                  </div>
                </div>
              )}
              
              {patient.email && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Mail className="h-5 w-5 text-[#0891B2]" />
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{patient.email}</p>
                  </div>
                </div>
              )}

              {patient.referral_source && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <User className="h-5 w-5 text-[#0891B2]" />
                  <div>
                    <p className="text-sm text-slate-500">¿Cómo se enteró?</p>
                    <p className="font-medium">{referralLabels[patient.referral_source] || patient.referral_source}</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Calendar className="h-5 w-5 text-[#0891B2]" />
                <div>
                  <p className="text-sm text-slate-500">Total de citas</p>
                  <p className="font-medium">{patient.total_appointments}</p>
                </div>
              </div>

              {patient.notes && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 font-medium mb-1">Notas</p>
                  <p className="text-sm text-slate-700">{patient.notes}</p>
                </div>
              )}

              {/* Evaluations Button */}
              <Button 
                onClick={() => navigate(`/admin/patients/${id}/evaluacion-kanban`)}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
              >
                <Eye className="h-4 w-4 mr-2" />
                Nueva Evaluación Oftalmológica
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Appointments & Sales */}
        <div className="lg:col-span-2 space-y-6">
          {/* Sales Summary Card */}
          <Card className="border-0 shadow-sm bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-green-600 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total consumido</p>
                    <p className="text-2xl font-bold text-green-700">
                      Q{totalSpent.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">{sales.filter(s => s.estado_pago !== 'ANULADA').length} compras</p>
                  <Link to="/admin/ventas" className="text-sm text-green-600 hover:underline">
                    Ver todas las ventas
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Appointment History */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#0891B2]" />
                Historial de Citas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {appointments.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No hay citas registradas</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.map((apt) => (
                    <div
                      key={apt.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/appointments/${apt.id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                          <Calendar className="h-5 w-5 text-[#0891B2]" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {format(new Date(apt.appointment_date + 'T12:00:00'), "d 'de' MMMM, yyyy", { locale: es })}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Clock className="h-3 w-3" />
                            <span>{apt.appointment_time}</span>
                            {apt.reason_text && (
                              <>
                                <span>•</span>
                                <span className="truncate max-w-[200px]">{apt.reason_text}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      {getStatusBadge(apt.status)}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sales History */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-green-600" />
                Historial de Ventas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sales.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No hay ventas registradas</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sales.map((sale) => (
                    <div
                      key={sale.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/ventas/${sale.id}`)}
                      data-testid={`sale-row-${sale.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                          <FileText className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{sale.numero_venta}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Calendar className="h-3 w-3" />
                            <span>
                              {format(new Date(sale.fecha_venta || sale.created_at), "d MMM yyyy", { locale: es })}
                            </span>
                            <span>•</span>
                            <span>{sale.lineas?.length || 0} ítem(s)</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">
                            Q{sale.total.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                          </p>
                          {sale.saldo_pendiente > 0 && (
                            <p className="text-xs text-amber-600">
                              Pendiente: Q{sale.saldo_pendiente.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                            </p>
                          )}
                        </div>
                        <Badge className={
                          sale.estado_pago === 'PAGADA' ? 'bg-green-100 text-green-700' :
                          sale.estado_pago === 'ANULADA' ? 'bg-red-100 text-red-700' :
                          sale.estado_pago === 'PARCIAL' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {sale.estado_pago}
                        </Badge>
                        <Eye className="h-4 w-4 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">Editar Paciente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo</Label>
              <Input
                value={editData.full_name || ''}
                onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                data-testid="edit-patient-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha de Nacimiento</Label>
                <Input
                  type="date"
                  value={editData.birth_date || ''}
                  onChange={(e) => setEditData({ ...editData, birth_date: e.target.value })}
                  data-testid="edit-patient-birth-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Sexo</Label>
                <Select
                  value={editData.sex || ''}
                  onValueChange={(value) => setEditData({ ...editData, sex: value })}
                >
                  <SelectTrigger data-testid="edit-patient-sex">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="masculino">Masculino</SelectItem>
                    <SelectItem value="femenino">Femenino</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={editData.phone || ''}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  placeholder="5555-5555"
                  data-testid="edit-patient-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono Alterno</Label>
                <Input
                  value={editData.alternate_phone || ''}
                  onChange={(e) => setEditData({ ...editData, alternate_phone: e.target.value })}
                  placeholder="Opcional"
                  data-testid="edit-patient-alternate-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editData.email || ''}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                data-testid="edit-patient-email"
              />
            </div>

            <div className="space-y-2">
              <Label>¿Cómo se enteró de nosotros?</Label>
              <Select
                value={editData.referral_source || ''}
                onValueChange={(value) => setEditData({ ...editData, referral_source: value })}
              >
                <SelectTrigger data-testid="edit-patient-referral">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recomendacion">Recomendación de otro paciente</SelectItem>
                  <SelectItem value="redes_sociales">Redes Sociales</SelectItem>
                  <SelectItem value="google">Google / Búsqueda</SelectItem>
                  <SelectItem value="publicidad">Publicidad</SelectItem>
                  <SelectItem value="clinica_cercana">Clínica cercana</SelectItem>
                  <SelectItem value="seguro_medico">Seguro Médico</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                rows={3}
                placeholder="Observaciones adicionales..."
                data-testid="edit-patient-notes"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="flex-1" onClick={() => setShowEdit(false)}>
                Cancelar
              </Button>
              <Button className="flex-1 bg-[#0891B2] hover:bg-[#0e7490]" onClick={handleUpdate} data-testid="save-patient-button">
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminar Paciente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">
              ¿Estás seguro de eliminar a <strong>{patient?.full_name}</strong>? Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                Cancelar
              </Button>
              <Button 
                variant="destructive" 
                className="flex-1" 
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Sí, Eliminar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientDetailPage;
